'use client'

import { useEffect, useRef, useState } from 'react'

type Msg = { role: 'user' | 'assistant'; content: string }

const GREETING =
  'Cześć! Jestem asystentem divemate 🤿 Pomogę dobrać sprzęt, sprawdzić dostępność lub odpowiem na pytanie o dostawę i zwroty. W czym mogę pomóc?'

const SUGGESTIONS = [
  'Jaką maskę polecasz na początek?',
  'Ile kosztuje dostawa?',
  'Jak działają zwroty?',
]

// Bezpieczne renderowanie odpowiedzi: linki [tekst](url) → <a>, **pogrubienie**, nowe linie.
// Budujemy węzły Reacta (bez dangerouslySetInnerHTML), więc XSS nie przejdzie.
function renderContent(text: string): React.ReactNode {
  return text.split('\n').map((line, li) => {
    const parts: React.ReactNode[] = []
    const regex = /\[([^\]]+)\]\(([^)]+)\)|\*\*([^*]+)\*\*/g
    let last = 0
    let m: RegExpExecArray | null
    let key = 0
    while ((m = regex.exec(line)) !== null) {
      if (m.index > last) parts.push(line.slice(last, m.index))
      if (m[1] && m[2]) {
        const href = m[2]
        const safe = href.startsWith('/') || href.startsWith('https://') || href.startsWith('http://') || href.startsWith('mailto:')
        parts.push(
          safe ? (
            <a key={key++} href={href} style={{ color: '#1FB8B0', textDecoration: 'underline' }}>
              {m[1]}
            </a>
          ) : (
            m[1]
          )
        )
      } else if (m[3]) {
        parts.push(
          <strong key={key++} style={{ color: '#fff' }}>
            {m[3]}
          </strong>
        )
      }
      last = regex.lastIndex
    }
    if (last < line.length) parts.push(line.slice(last))
    return (
      <span key={li}>
        {parts}
        {li < text.split('\n').length - 1 && <br />}
      </span>
    )
  })
}

export function AssistantWidget() {
  const [enabled, setEnabled] = useState(false)
  const [open, setOpen] = useState(false)
  const [messages, setMessages] = useState<Msg[]>([])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const scrollRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    let alive = true
    fetch('/api/assistant/status')
      .then((r) => r.json())
      .then((d) => { if (alive) setEnabled(Boolean(d?.enabled)) })
      .catch(() => {})
    return () => { alive = false }
  }, [])

  useEffect(() => {
    if (open && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight
    }
  }, [messages, open, loading])

  useEffect(() => {
    if (open) inputRef.current?.focus()
  }, [open])

  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') setOpen(false) }
    if (open) window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open])

  async function send(text: string) {
    const content = text.trim()
    if (!content || loading) return
    const next: Msg[] = [...messages, { role: 'user', content }]
    setMessages(next)
    setInput('')
    setLoading(true)
    try {
      const res = await fetch('/api/assistant/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: next }),
      })
      const data = await res.json()
      const reply =
        typeof data?.reply === 'string'
          ? data.reply
          : typeof data?.error === 'string'
            ? data.error
            : 'Przepraszam, coś poszło nie tak. Napisz na kontakt@divemate.pl.'
      setMessages((m) => [...m, { role: 'assistant', content: reply }])
    } catch {
      setMessages((m) => [...m, { role: 'assistant', content: 'Brak połączenia. Spróbuj ponownie za chwilę.' }])
    } finally {
      setLoading(false)
    }
  }

  if (!enabled) return null

  return (
    <>
      {/* Floating button */}
      <button
        onClick={() => setOpen((o) => !o)}
        aria-label={open ? 'Zamknij czat z asystentem' : 'Otwórz czat z asystentem'}
        aria-expanded={open}
        className="assistant-fab"
      >
        {open ? (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M18 6 6 18M6 6l12 12" />
          </svg>
        ) : (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8z" />
          </svg>
        )}
      </button>

      {/* Panel */}
      {open && (
        <div className="assistant-panel" role="dialog" aria-label="Czat z asystentem divemate">
          {/* Header */}
          <div className="assistant-header">
            <div className="assistant-avatar" aria-hidden="true">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#03080F" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 8V4H8M4 8h16a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2zM9 14h.01M15 14h.01" />
              </svg>
            </div>
            <div>
              <p className="assistant-title">Asystent divemate</p>
              <p className="assistant-subtitle">Pomogę dobrać sprzęt i odpowiem na pytania</p>
            </div>
          </div>

          {/* Messages */}
          <div className="assistant-messages" ref={scrollRef}>
            <div className="assistant-bubble assistant-bubble-ai">{renderContent(GREETING)}</div>

            {messages.length === 0 && (
              <div className="assistant-suggestions">
                {SUGGESTIONS.map((s) => (
                  <button key={s} className="assistant-chip" onClick={() => send(s)} disabled={loading}>
                    {s}
                  </button>
                ))}
              </div>
            )}

            {messages.map((m, i) => (
              <div
                key={i}
                className={`assistant-bubble ${m.role === 'user' ? 'assistant-bubble-user' : 'assistant-bubble-ai'}`}
              >
                {renderContent(m.content)}
              </div>
            ))}

            {loading && (
              <div className="assistant-bubble assistant-bubble-ai assistant-typing" aria-label="Asystent pisze">
                <span></span><span></span><span></span>
              </div>
            )}
          </div>

          {/* Input */}
          <form
            className="assistant-input-row"
            onSubmit={(e) => { e.preventDefault(); send(input) }}
          >
            <input
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Napisz wiadomość…"
              aria-label="Twoja wiadomość"
              maxLength={500}
              className="assistant-input"
            />
            <button type="submit" className="assistant-send" disabled={loading || !input.trim()} aria-label="Wyślij">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="m22 2-7 20-4-9-9-4Z" /><path d="M22 2 11 13" />
              </svg>
            </button>
          </form>
          <p className="assistant-disclaimer">Asystent AI — może się mylić. W razie wątpliwości napisz na kontakt@divemate.pl.</p>
        </div>
      )}
    </>
  )
}
